import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Building2, TrendingUp, Search, Plus,
    Wallet, CreditCard, Banknote
} from 'lucide-react';
import { Button, CardStats } from '@components/ui';
import { CardGridSkeleton } from '@components/skeleton';
import InsaatOlusturModal from '../../Insaat/Projeler/InsaatOlusturModal';
import FinProjeKarti from './components/FinProjeKarti';

// Mock Data Imports
import { getApartmentsByConstructionId } from '@mocks/daireler';
import { getBlocksByConstructionId } from '@mocks/bloklar';

export default function FinansProjeler() {
    const navigate = useNavigate();
    const { constructions } = useSelector((state) => state.construction);

    const [modalOpen, setModalOpen] = useState(false);
    const [expandedInsaat, setExpandedInsaat] = useState(null);
    const [expandedBlok, setExpandedBlok] = useState(null);
    const [filterDurum, setFilterDurum] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Formatlayıcı
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatCompact = (amount) => {
        if (amount >= 1000000) return `₺${(amount / 1000000).toFixed(1)}M`;
        if (amount >= 1000) return `₺${(amount / 1000).toFixed(0)}K`;
        return formatCurrency(amount);
    };

    // Global İstatistik Hesaplama
    const globalStats = useMemo(() => {
        let totalSatis = 0;
        let totalTahsilat = 0;
        let totalAlacak = 0;
        let totalSatilan = 0;

        // Her inşaat için hesapla
        const projectStats = constructions.map(c => {
            const daireler = getApartmentsByConstructionId(c.id);
            const soldApartments = daireler.filter(d => d.status === 'sold' || d.status === 'reserved');

            const pStats = {
                id: c.id,
                toplamSatis: soldApartments.reduce((acc, d) => acc + d.totalPrice, 0),
                toplamTahsilat: soldApartments.reduce((acc, d) => acc + d.paidAmount, 0),
                kalanAlacak: soldApartments.reduce((acc, d) => acc + d.remainingAmount, 0),
                toplamDaire: daireler.length,
                toplamSatilan: soldApartments.length,
            };

            // Beklenen gelir = Toplam Satış Bedeli
            pStats.beklenenGelir = pStats.toplamSatis;

            // Global toplama ekle
            totalSatis += pStats.toplamSatis;
            totalTahsilat += pStats.toplamTahsilat;
            totalAlacak += pStats.kalanAlacak;
            totalSatilan += pStats.toplamSatilan;

            return pStats;
        });

        return {
            totalSatis,
            totalTahsilat,
            totalAlacak,
            totalSatilan,
            projectStats
        };
    }, [constructions]);

    // Yükleniyor durumu (simüle edilmiş)
    const isLoading = false;
    if (isLoading) return <CardGridSkeleton variant="construction" cardCount={2} columns={1} />;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Proje Finansalları</h1>
                    <p className="text-text-tertiary text-sm mt-1">
                        Satış, tahsilat ve alacak durumlarının proje bazlı takibi
                    </p>
                </div>
                <Button
                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={() => setModalOpen(true)}
                >
                    Yeni Proje
                </Button>
            </div>

            {/* Global Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Satış Hacmi"
                    value={formatCompact(globalStats.totalSatis)}
                    icon={TrendingUp}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Toplam Tahsilat"
                    value={formatCompact(globalStats.totalTahsilat)}
                    icon={Wallet}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Kalan Alacak"
                    value={formatCompact(globalStats.totalAlacak)}
                    icon={CreditCard}
                    color="warning"
                    size="sm"
                />
                <CardStats
                    title="Satış Adedi"
                    value={globalStats.totalSatilan}
                    icon={Banknote}
                    color="info"
                    size="sm"
                />
            </div>

            {/* Filtreleme */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                        type="text"
                        placeholder="Daire no veya alıcı adı ara..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                    />
                </div>
                <select
                    value={filterDurum}
                    onChange={e => setFilterDurum(e.target.value)}
                    className="px-4 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                >
                    <option value="">Tüm Durumlar</option>
                    <option value="sold">Satılanlar</option>
                    <option value="reserved">Rezerve</option>
                    <option value="available">Boş</option>
                </select>
            </div>

            {/* Proje Listesi (Accordion) */}
            <div className="space-y-4">
                {constructions.map(insaat => {
                    const stats = globalStats.projectStats.find(p => p.id === insaat.id);
                    return (
                        <FinProjeKarti
                            key={insaat.id}
                            insaat={insaat}
                            stats={stats}
                            isExpanded={expandedInsaat === insaat.id}
                            onToggle={() => setExpandedInsaat(expandedInsaat === insaat.id ? null : insaat.id)}
                            expandedBlok={expandedBlok}
                            setExpandedBlok={setExpandedBlok}
                            filterDurum={filterDurum}
                            searchTerm={searchTerm}
                            navigate={navigate}
                            formatCurrency={formatCurrency}
                        />
                    );
                })}
            </div>

            {/* Yeni Proje Modalı */}
            <InsaatOlusturModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
            />
        </div>
    );
}
